import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  Fingerprint,
  KeyRound,
  Loader2,
  LockKeyhole,
  Rocket,
  ServerCog,
  ShieldCheck,
  Workflow,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import {
  useBosunWorkspaceCredentialStatus,
  useBosunTokenInventory,
  useCreateBosunProject,
  useSaveBosunWorkspaceCredentials,
  useUpdateWorkspace,
  useWorkspaces,
  type BosunTokenInventoryRead,
  type BosunOrgCredentials,
  type BosunProjectCreate,
  type BosunWorkspaceCredentialStatusRead,
  type WorkspaceRead,
} from '@/api/queries'
import { canManageMembers } from '@/api/workspace-permissions'
import ErrorFallback from '@/components/ErrorFallback'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Textarea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { formatError } from '@/lib/format-error'

export const Route = createFileRoute('/_authenticated/bosun')({
  component: BosunCompatibilityRedirect,
  errorComponent: ErrorFallback,
})

const EMPTY_PROJECT_FORM = {
  name: '',
  slug: '',
  description: '',
  slackInviteUserIds: '',
}

const EMPTY_CREDENTIAL_FORM = {
  flyToken: '',
  neonKey: '',
  cloudflareToken: '',
  cloudflareAccountId: '',
  cloudflareZoneId: '',
  openrouterKey: '',
  slackBotToken: '',
  slackAppToken: '',
  slackAppConfigToken: '',
  slackAppConfigRefreshToken: '',
  githubToken: '',
}

type ProjectFormState = typeof EMPTY_PROJECT_FORM
type CredentialFormState = typeof EMPTY_CREDENTIAL_FORM

const INTERNAL_PROJECT_DEFAULTS = {
  githubOwner: 'AI-Escape',
  templateRepo: 'AI-Escape/aiescape-spa-template',
  primaryDomain: 'aiescapelabs.com',
}
const SLACK_APP_NAME_MAX_LENGTH = 35
const SLACK_BOT_DISPLAY_NAME_MAX_LENGTH = 80
const PM_NAME_SUFFIX = ' PM'

function deriveProjectSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .split('-')
    .filter((part) => part && part !== 'and')
    .join('-')
}

function cleanSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function slackChannelsFor(slug: string) {
  return {
    stakeholder: `${slug}-stakeholder`,
    intake: `${slug}-intake`,
    ops: `${slug}-ops`,
    tasks: `${slug}-tasks`,
    jobs: `${slug}-jobs`,
  }
}

function slackPmName(projectName: string, fallbackSlug: string, maxLength: number): string {
  let name = projectName.trim().replace(/\s+/g, ' ') || fallbackSlug
  const available = maxLength - PM_NAME_SUFFIX.length
  if (name.length > available) {
    name = name.slice(0, available).trimEnd() || name.slice(0, available)
  }
  return `${name}${PM_NAME_SUFFIX}`
}

function slackAppIdentityFor(projectName: string, slug: string) {
  return {
    app: slackPmName(projectName, slug, SLACK_APP_NAME_MAX_LENGTH),
    bot: slackPmName(projectName, slug, SLACK_BOT_DISPLAY_NAME_MAX_LENGTH),
  }
}

function slackInviteUserIds(value: string): string[] {
  const seen = new Set<string>()
  return value
    .replace(/,/g, ' ')
    .split(/\s+/)
    .map((entry) => entry.trim().toUpperCase())
    .filter(Boolean)
    .filter((entry) => {
      if (seen.has(entry)) return false
      seen.add(entry)
      return true
    })
}

function adminWorkspaces(workspaces: WorkspaceRead[] | undefined) {
  return (workspaces ?? []).filter(
    (workspace) => canManageMembers(workspace.role) && workspace.archive_status !== 'archived',
  )
}

function toPayload(form: ProjectFormState): BosunProjectCreate {
  const slug = cleanSlug(form.slug) || deriveProjectSlug(form.name)
  return {
    name: form.name,
    slug,
    description: form.description.trim() || null,
    github_owner: INTERNAL_PROJECT_DEFAULTS.githubOwner,
    template_repo: INTERNAL_PROJECT_DEFAULTS.templateRepo,
    primary_domain: INTERNAL_PROJECT_DEFAULTS.primaryDomain,
    app_subdomain: slug,
    api_subdomain: `api.${slug}`,
    github_provisioning_mode: 'live',
    provider_provisioning_mode: 'live',
    slack_channels: slackChannelsFor(slug),
    slack_default_invite_user_ids: slackInviteUserIds(form.slackInviteUserIds),
  }
}

function toWorkspaceCredentials(form: CredentialFormState): BosunOrgCredentials {
  return {
    fly_api_token: form.flyToken || null,
    neon_api_key: form.neonKey || null,
    cloudflare_api_token: form.cloudflareToken || null,
    cloudflare_account_id: form.cloudflareAccountId || null,
    cloudflare_zone_id: form.cloudflareZoneId || null,
    openrouter_api_key: form.openrouterKey || null,
    slack_bot_token: form.slackBotToken || null,
    slack_app_token: form.slackAppToken || null,
    slack_app_config_token: form.slackAppConfigToken || null,
    slack_app_config_refresh_token: form.slackAppConfigRefreshToken || null,
    github_developer_token: form.githubToken || null,
  }
}

function statusTone(status: string | undefined): React.ComponentProps<typeof Badge>['tone'] {
  if (status === 'valid') return 'success'
  if (status === 'invalid') return 'danger'
  return 'warning'
}

function CredentialValidationPanel({
  validation,
}: {
  validation: BosunWorkspaceCredentialStatusRead
}) {
  return (
    <div className="space-y-2 rounded-md border border-white/10 bg-white/[0.025] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={validation.ready ? 'success' : 'warning'}>
          {validation.ready ? 'ready' : 'incomplete'}
        </Badge>
        <span className="text-sm text-muted-foreground">{validation.secret_persistence}</span>
      </div>
      <p className="text-xs text-muted-foreground">
        {validation.r2_credential_source_explanation ??
          'Generated Hermes R2 S3 keys are Bosun-minted from the workspace Cloudflare operator token; users do not supply separate project R2 keys.'}
      </p>
      {validation.last_r2_validation_status ? (
        <p className="text-xs text-muted-foreground">
          R2 live validation: {validation.last_r2_validation_status}
          {validation.last_r2_validation_failure_class
            ? ` (${validation.last_r2_validation_failure_class})`
            : ''}
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-3">
        {validation.setup_order.map((provider) => (
          <div
            key={provider}
            className="flex items-center justify-between rounded-md bg-background/70 px-3 py-2 text-sm"
          >
            <span className="capitalize">{provider}</span>
            <Badge tone={statusTone(validation.provider_status[provider])} size="xs">
              {validation.provider_status[provider] ?? 'missing'}
            </Badge>
          </div>
        ))}
      </div>
      {validation.missing.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          Missing: {validation.missing.join(', ')}
        </p>
      ) : null}
    </div>
  )
}

function TokenInventoryPanel({
  inventory,
}: {
  inventory: BosunTokenInventoryRead
}) {
  const tokenItems = inventory.token_items ?? []
  const cloudflareChecks = inventory.cloudflare_checks ?? []
  const cleanupBindings = inventory.cleanup_bindings ?? []
  const manualItems = tokenItems.filter((item) => item.status === 'manual_review')
  const failedChecks = cloudflareChecks.filter((check) => !check.success)

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Token inventory"
        description="No-secret operator view of Cloudflare/R2 token classes, generated runtime fingerprint, and cleanup binding status."
      />
      <div className="grid gap-3 lg:grid-cols-3">
        <div className="rounded-md border border-white/10 bg-white/[0.025] p-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Fingerprint className="size-4 text-muted-foreground" />
            BOSUN_DOT_ENV
          </div>
          <dl className="mt-3 grid gap-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge tone={inventory.bosun_dot_env.status === 'present' ? 'success' : 'warning'} size="xs">
                  {inventory.bosun_dot_env.status}
                </Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-muted-foreground">Keys</dt>
              <dd>{inventory.bosun_dot_env.key_count}</dd>
            </div>
            <div className="grid gap-1">
              <dt className="text-muted-foreground">Fingerprint</dt>
              <dd className="break-all font-mono text-xs">
                {inventory.bosun_dot_env.fingerprint_sha256 ?? 'none'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-md border border-white/10 bg-white/[0.025] p-3">
          <div className="text-sm font-medium">Cloudflare checks</div>
          <div className="mt-3 space-y-2">
            {cloudflareChecks.map((check) => (
              <div key={`${check.name}:${check.path}`} className="grid gap-1 rounded bg-background/70 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{check.name}</span>
                  <Badge tone={check.success ? 'success' : 'danger'} size="xs">
                    {check.status_code ?? 'n/a'}
                  </Badge>
                </div>
                <div className="break-all text-muted-foreground">
                  {check.method} {check.path}
                </div>
                {(check.error_codes ?? []).length > 0 ? (
                  <div className="text-destructive-soft-foreground">
                    codes {(check.error_codes ?? []).join(', ')}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-white/10 bg-white/[0.025] p-3">
          <div className="text-sm font-medium">Cleanup binding</div>
          <div className="mt-3 space-y-2">
            {cleanupBindings.map((binding) => (
              <div key={binding.name} className="grid gap-1 rounded bg-background/70 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{binding.name}</span>
                  <Badge tone={binding.status === 'dedicated' ? 'success' : 'warning'} size="xs">
                    {binding.status}
                  </Badge>
                </div>
                {binding.digest ? (
                  <div className="break-all font-mono text-muted-foreground">{binding.digest}</div>
                ) : null}
                {binding.note ? <div className="text-muted-foreground">{binding.note}</div> : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      {tokenItems.length > 0 ? (
        <div className="overflow-hidden rounded-md border border-white/10">
          <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-3 bg-white/[0.04] px-3 py-2 text-xs font-medium text-muted-foreground">
            <span>Name</span>
            <span>Class</span>
            <span>Status</span>
            <span>Digest</span>
          </div>
          {tokenItems.map((item) => (
            <div
              key={`${item.token_class}:${item.name}:${item.digest ?? ''}`}
              className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-3 border-t border-white/10 px-3 py-2 text-xs"
            >
              <span className="min-w-0 break-words font-medium">{item.name}</span>
              <span className="min-w-0 break-words text-muted-foreground">{item.token_class}</span>
              <span>
                <Badge tone={item.status === 'manual_review' ? 'warning' : 'success'} size="xs">
                  {item.status}
                </Badge>
              </span>
              <span className="min-w-0 break-all font-mono text-muted-foreground">
                {item.digest ?? 'none'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <Alert tone="info">
          <AlertDescription>No Cloudflare R2 API tokens matched the Bosun inventory filters.</AlertDescription>
        </Alert>
      )}

      {manualItems.length > 0 || failedChecks.length > 0 ? (
        <Alert tone="warning">
          <AlertDescription>
            {manualItems.length > 0
              ? `${manualItems.length} legacy or wildcard R2 token${manualItems.length === 1 ? '' : 's'} need dependency mapping before revoke or rotation.`
              : 'One or more Cloudflare inventory checks failed; inspect endpoint status and error codes above.'}
          </AlertDescription>
        </Alert>
      ) : null}
    </section>
  )
}

function CredentialOnboarding({
  workspace,
  status,
}: {
  workspace: WorkspaceRead
  status: BosunWorkspaceCredentialStatusRead | null
}) {
  const [form, setForm] = useState<CredentialFormState>(EMPTY_CREDENTIAL_FORM)
  const saveCredentials = useSaveBosunWorkspaceCredentials(workspace.uuid)
  const fieldRequired = !status?.ready
  const set = (key: keyof CredentialFormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }))
  }
  const save = async () => {
    const result = await saveCredentials.mutateAsync(toWorkspaceCredentials(form))
    if (result.ready) {
      toast.success('Workspace credentials saved')
      setForm(EMPTY_CREDENTIAL_FORM)
    } else {
      toast.error('Workspace credentials need attention')
    }
  }

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Workspace credential onboarding"
        description="Manage shared provider credentials for this workspace. R2 access keys are hidden: Bosun derives one workspace R2 credential from the Cloudflare operator token and reuses it for generated projects with project-specific buckets."
      />
      {status ? <CredentialValidationPanel validation={status} /> : null}
      <Alert tone={status?.ready ? 'success' : 'warning'}>
        <AlertDescription>
          {status?.ready
            ? 'Provisioning is enabled for this workspace. Enter new values only when rotating or repairing credentials.'
            : 'Project creation is gated until all workspace credential providers are valid.'}
        </AlertDescription>
      </Alert>
      <div className="grid gap-4 md:grid-cols-2">
        <SecretField label="Fly token" value={form.flyToken} onChange={(v) => set('flyToken', v)} required={fieldRequired} />
        <SecretField label="Neon key" value={form.neonKey} onChange={(v) => set('neonKey', v)} required={fieldRequired} />
        <SecretField label="Cloudflare operator token" value={form.cloudflareToken} onChange={(v) => set('cloudflareToken', v)} required={fieldRequired} />
        <SecretField label="GitHub developer token" value={form.githubToken} onChange={(v) => set('githubToken', v)} required={fieldRequired} />
        <SecretField label="Slack bot token" value={form.slackBotToken} onChange={(v) => set('slackBotToken', v)} required={fieldRequired} />
        <SecretField label="Slack app token" value={form.slackAppToken} onChange={(v) => set('slackAppToken', v)} required={fieldRequired} />
        <SecretField label="Slack app config access token" value={form.slackAppConfigToken} onChange={(v) => set('slackAppConfigToken', v)} required={fieldRequired} />
        <SecretField label="Slack app config refresh token" value={form.slackAppConfigRefreshToken} onChange={(v) => set('slackAppConfigRefreshToken', v)} required={fieldRequired} />
        <Field label="Cloudflare account id">
          <Input value={form.cloudflareAccountId} onChange={(event) => set('cloudflareAccountId', event.target.value)} required={fieldRequired} />
        </Field>
        <Field label="Cloudflare zone id">
          <Input value={form.cloudflareZoneId} onChange={(event) => set('cloudflareZoneId', event.target.value)} required={fieldRequired} />
        </Field>
        <SecretField label="OpenRouter key" value={form.openrouterKey} onChange={(v) => set('openrouterKey', v)} required={fieldRequired} />
      </div>
      {saveCredentials.error ? (
        <Alert tone="danger" role="alert">
          <AlertDescription>{formatError(saveCredentials.error)}</AlertDescription>
        </Alert>
      ) : null}
      <Button type="button" variant="secondary" onClick={save} disabled={saveCredentials.isPending}>
        {saveCredentials.isPending ? <Loader2 className="animate-spin" /> : <ShieldCheck />}
        {status?.ready ? 'Update workspace credentials' : 'Save workspace credentials'}
      </Button>
    </section>
  )
}

function SlackDefaultInviteSettings({ workspace }: { workspace: WorkspaceRead }) {
  const [value, setValue] = useState(() =>
    (workspace.slack_default_invite_user_ids ?? []).join(' '),
  )
  const updateWorkspace = useUpdateWorkspace(workspace.uuid)

  const save = async () => {
    await updateWorkspace.mutateAsync({
      slack_default_invite_user_ids: slackInviteUserIds(value),
    })
    toast.success('Default Slack invited users saved')
  }

  const clear = async () => {
    setValue('')
    await updateWorkspace.mutateAsync({ slack_default_invite_user_ids: [] })
    toast.success('Default Slack invited users cleared')
  }

  return (
    <section className="space-y-3">
      <SectionHeader
        title="Slack setup"
        description="Default Slack invited users apply only to new projects. Existing project invite lists are unchanged unless edited on that project."
      />
      <Field label="Default Slack invited users">
        <Input
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="U06G4CJ7F3L U012ABCDEF0"
          title="Enter one or more Slack user IDs such as U06G4CJ7F3L, separated by spaces or commas."
        />
      </Field>
      {updateWorkspace.error ? (
        <Alert tone="danger" role="alert">
          <AlertDescription>{formatError(updateWorkspace.error)}</AlertDescription>
        </Alert>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={save} disabled={updateWorkspace.isPending}>
          {updateWorkspace.isPending ? <Loader2 className="animate-spin" /> : <ShieldCheck />}
          Save default
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={clear}
          disabled={updateWorkspace.isPending || slackInviteUserIds(value).length === 0}
        >
          Clear
        </Button>
      </div>
    </section>
  )
}

function ProvisioningForm({
  workspace,
  credentialStatus,
}: {
  workspace: WorkspaceRead
  credentialStatus: BosunWorkspaceCredentialStatusRead | null
}) {
  const [form, setForm] = useState<ProjectFormState>(() => ({
    ...EMPTY_PROJECT_FORM,
    slackInviteUserIds: (workspace.slack_default_invite_user_ids ?? []).join(' '),
  }))
  const [slugEdited, setSlugEdited] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitLocked = useRef(false)
  const createProject = useCreateBosunProject(workspace.uuid)
  const navigate = useNavigate()

  const set = (key: keyof ProjectFormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const setProjectName = (value: string) => {
    setForm((current) => ({
      ...current,
      name: value,
      slug: slugEdited ? current.slug : deriveProjectSlug(value),
    }))
  }

  const setSlug = (value: string) => {
    setSlugEdited(true)
    set('slug', cleanSlug(value))
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!credentialStatus?.ready) {
      toast.error('Complete workspace credential onboarding before creating a project')
      return
    }
    if (submitLocked.current) return
    submitLocked.current = true
    setIsSubmitting(true)
    try {
      const project = await createProject.mutateAsync(toPayload(form))
      toast.success('Bosun live provisioning queued')
      setForm(EMPTY_PROJECT_FORM)
      setSlugEdited(false)
      await navigate({
        to: '/projects/$workspaceUuid/$projectUuid/provisioning',
        params: { workspaceUuid: project.workspace_uuid, projectUuid: project.uuid },
      })
    } finally {
      submitLocked.current = false
      setIsSubmitting(false)
    }
  }

  const slug = cleanSlug(form.slug) || deriveProjectSlug(form.name)
  const previewChannels = slug ? slackChannelsFor(slug) : null
  const previewIdentity = slug ? slackAppIdentityFor(form.name, slug) : null
  const canCreate = Boolean(credentialStatus?.ready && form.name.trim() && slug)

  return (
    <form className="space-y-6" onSubmit={onSubmit}>
      <section className="space-y-3">
        <SectionHeader
          title="New project"
          description="Give the PM enough context to route the work, then Bosun will provision the live stack with the existing workspace credentials."
        />
        {!credentialStatus?.ready ? (
          <Alert tone="warning">
            <AlertDescription>
              Workspace credentials must be ready before project provisioning can start.
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Project name">
            <Input
              value={form.name}
              onChange={(event) => setProjectName(event.target.value)}
              required
            />
          </Field>
          <Field label="Project slug">
            <Input
              value={form.slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder="derived-from-project-name"
              required
            />
          </Field>
          <Field label="Description">
            <Textarea
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
              placeholder="What should this project deliver, who is it for, and what does ready look like?"
              rows={3}
            />
          </Field>
          <Field label="Default Slack invite user IDs">
            <Input
              value={form.slackInviteUserIds}
              onChange={(event) => set('slackInviteUserIds', event.target.value)}
              placeholder="U06G4CJ7F3L U012ABCDEF0"
              title="Enter one or more Slack user IDs such as U06G4CJ7F3L, separated by spaces or commas."
            />
          </Field>
        </div>
        <SlackChannelPreview channels={previewChannels} identity={previewIdentity} />
        <div className="grid gap-2 rounded-md border border-white/10 bg-white/[0.025] p-3 text-sm sm:grid-cols-4">
          {['Received', 'Routed', 'Provisioning', 'Hermes startup'].map((label, index) => (
            <div key={label} className="flex items-center gap-2 text-muted-foreground">
              <span className="flex size-6 items-center justify-center rounded-full border border-white/10 bg-black/20 text-xs">
                {index + 1}
              </span>
              {label}
            </div>
          ))}
        </div>
      </section>

      {createProject.error ? (
        <Alert tone="danger" role="alert">
          <AlertDescription>{formatError(createProject.error)}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          disabled={isSubmitting || createProject.isPending || !canCreate}
        >
          {isSubmitting || createProject.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Rocket />
          )}
          Create live stack
        </Button>
      </div>
    </form>
  )
}

function SlackChannelPreview({
  channels,
  identity,
}: {
  channels: ReturnType<typeof slackChannelsFor> | null
  identity: ReturnType<typeof slackAppIdentityFor> | null
}) {
  return (
    <div className="space-y-2 rounded-md border border-white/10 bg-white/[0.025] p-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Workflow className="size-4 text-muted-foreground" />
        Slack app preview
      </div>
      {channels && identity ? (
        <div className="space-y-3">
          <dl className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
            <div className="flex justify-between gap-3">
              <dt>App</dt>
              <dd>{identity.app}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt>Bot</dt>
              <dd>{identity.bot}</dd>
            </div>
          </dl>
          <dl className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
            {Object.entries(channels).map(([role, channel]) => (
              <div key={role} className="flex justify-between gap-3">
                <dt className="capitalize">{role}</dt>
                <dd>#{channel}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">App identity and channels derive from the project name.</p>
      )}
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <label className="grid gap-2">
      <Label>{label}</Label>
      {children}
    </label>
  )
}

function SecretField({
  label,
  value,
  onChange,
  required = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
}) {
  return (
    <Field label={label}>
      <div className="relative">
        <Input
          type="password"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="pr-10"
          required={required}
        />
        <LockKeyhole className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      </div>
    </Field>
  )
}

function BosunCompatibilityRedirect() {
  const { data: workspaces, isLoading, error } = useWorkspaces()
  const navigate = useNavigate()
  const workspace = useMemo(() => adminWorkspaces(workspaces)[0], [workspaces])

  useEffect(() => {
    if (!workspace) return
    void navigate({
      to: '/projects/$workspaceUuid/new',
      params: { workspaceUuid: workspace.uuid },
      replace: true,
    })
  }, [navigate, workspace])

  if (isLoading) {
    return <Skeleton className="mx-auto mt-8 h-28 w-full max-w-3xl" />
  }
  if (error) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6 md:px-8 md:py-8">
        <Alert tone="danger" role="alert">
          <AlertDescription>Failed to load workspaces: {formatError(error)}</AlertDescription>
        </Alert>
      </div>
    )
  }
  if (!workspace) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6 md:px-8 md:py-8">
        <EmptyState
          icon={ServerCog}
          title="No admin workspace"
          description="Project provisioning requires workspace admin access."
        />
      </div>
    )
  }
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 md:px-8 md:py-8">
      <Alert tone="info">
        <AlertDescription className="flex items-center gap-2">
          Opening workspace project setup <ArrowRight className="size-4" />
        </AlertDescription>
      </Alert>
    </div>
  )
}

export function BosunProvisioningPage({
  workspaceUuid,
}: {
  workspaceUuid?: string
} = {}) {
  const { data: workspaces, isLoading, error } = useWorkspaces()
  const manageable = useMemo(() => adminWorkspaces(workspaces), [workspaces])
  const workspace = workspaceUuid
    ? manageable.find((candidate) => candidate.uuid === workspaceUuid)
    : manageable[0]
  const {
    data: credentialStatus,
    isLoading: credentialsLoading,
    error: credentialsError,
  } = useBosunWorkspaceCredentialStatus(workspace?.uuid)
  const {
    data: tokenInventory,
    isLoading: tokenInventoryLoading,
    error: tokenInventoryError,
  } = useBosunTokenInventory(workspace?.uuid)

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 md:px-8 md:py-8">
      <section className="relative overflow-hidden rounded-lg border border-white/10 bg-white/[0.035] p-5 shadow-floating backdrop-blur-xl md:p-7">
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"
        />
        <PageHeader
          eyebrow="Workspace project setup"
          title="Create a Bosun project"
          description="Set workspace credentials once, then provision project stacks from the workspace projects area."
          actions={
            workspace ? (
              <Badge tone={credentialStatus?.ready ? 'success' : 'warning'}>
                credentials {credentialStatus?.ready ? 'ready' : 'needed'}
              </Badge>
            ) : undefined
          }
        />
      </section>

      {isLoading ? (
        <Skeleton className="h-28 w-full" />
      ) : error ? (
        <Alert tone="danger" role="alert">
          <AlertDescription>Failed to load workspaces: {formatError(error)}</AlertDescription>
        </Alert>
      ) : !workspace ? (
        <EmptyState
          icon={ServerCog}
          title={workspaceUuid ? 'Workspace unavailable' : 'No admin workspace'}
          description="Bosun provisioning requires an active workspace where you can manage members."
        />
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <KeyRound className="size-5 text-muted-foreground" />
                <CardTitle>{workspace.name}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {credentialsLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : credentialsError ? (
                <Alert tone="danger" role="alert">
                  <AlertDescription>
                    Failed to load workspace credentials: {formatError(credentialsError)}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-6">
                  <CredentialOnboarding workspace={workspace} status={credentialStatus ?? null} />
                  {tokenInventoryLoading ? (
                    <Skeleton className="h-48 w-full" />
                  ) : tokenInventoryError ? (
                    <Alert tone="danger" role="alert">
                      <AlertDescription>
                        Failed to load token inventory: {formatError(tokenInventoryError)}
                      </AlertDescription>
                    </Alert>
                  ) : tokenInventory ? (
                    <TokenInventoryPanel inventory={tokenInventory} />
                  ) : null}
                  <SlackDefaultInviteSettings
                    key={`${workspace.uuid}:${(workspace.slack_default_invite_user_ids ?? []).join(',')}`}
                    workspace={workspace}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <ServerCog className="size-5 text-muted-foreground" />
                <CardTitle>Project provisioning</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ProvisioningForm
                key={`${workspace.uuid}:${(workspace.slack_default_invite_user_ids ?? []).join(',')}`}
                workspace={workspace}
                credentialStatus={credentialStatus ?? null}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
